import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useStoreStatus } from "@/context/StoreStatusContext";
import { Minus, Plus } from "lucide-react";
import LaunchPromoBanner from "@/components/site/LaunchPromoBanner";
import WelcomeOfferBanner from "@/components/site/WelcomeOfferBanner";
import { cldImage } from "@/lib/cldImage";

const UPGRADE = {
  1: { size: 2, prompt: "One more and it becomes a moment for two.",     label: "Upgrade to For Two" },
  2: { size: 4, prompt: "Two more cups and it becomes a Sharing Box.",    label: "Upgrade to To Share" },
  4: { size: 6, prompt: "Two more cups and it's ready for the occasion.", label: "Upgrade to For the Occasion" },
};

function BoxBreakdown({ selections }) {
  if (!selections?.length) return null;
  const grouped = selections.reduce((acc, s) => ({ ...acc, [s]: (acc[s] || 0) + 1 }), {});
  return (
    <ul className="mt-2 space-y-1 text-sm text-espresso-light" data-testid="box-breakdown">
      {Object.entries(grouped).map(([flavor, n]) => (
        <li key={flavor} className="flex items-baseline gap-2">
          <span className="text-gold">·</span>
          <span>{flavor}</span>
          <span className="text-espresso-light/70">× {n}</span>
        </li>
      ))}
    </ul>
  );
}

function Upsell({ item }) {
  const size = item.selections?.length;
  const up = UPGRADE[size];
  if (!up || item.category !== "boxes") return null;
  return (
    <div className="mt-4 border border-gold/40 bg-gold/5 px-5 py-4 flex items-center justify-between gap-4" data-testid={`cart-upsell-${size}`}>
      <p className="font-serif italic text-espresso text-sm md:text-base">{up.prompt}</p>
      <Link to={`/box/${up.size}`} className="label-xs link-underline text-espresso whitespace-nowrap">
        {up.label} →
      </Link>
    </div>
  );
}

export default function Cart() {
  const { items, subtotal, updateQty, removeItem } = useCart();
  const { storeStatus } = useStoreStatus();
  const isOpen = !storeStatus || storeStatus.is_open;

  return (
    <main data-testid="cart-page" className="mx-auto max-w-[1400px] px-4 pb-28 pt-9 md:px-10 md:py-24">
      <div className="border-b border-line pb-10 grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-8">
          <div className="label-xs text-espresso-light">Basket</div>
          <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-[0.95] tracking-tight text-espresso">Your basket.</h1>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="py-24 text-center">
          <p className="font-serif italic text-2xl text-espresso-light">Your basket is quietly waiting.</p>
          <Link to="/menu" className="mt-8 inline-block btn-espresso" data-testid="cart-empty-cta">Choose a box</Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-12 gap-10 mt-10">
          <div className="md:col-span-8 space-y-8">
            {items.map((it) => (
              <div key={it._key} className="grid grid-cols-[80px_1fr] sm:grid-cols-[100px_1fr_auto] gap-4 sm:gap-6 border-b border-line pb-6" data-testid={`cart-line-${it.slug}`}>
                <div className="aspect-[4/5] bg-ivory-100 overflow-hidden">
                  {it.image_url && <img src={cldImage(it.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${it.image_url}` : it.image_url, { w: 240 })} alt={it.name} className="w-full h-full object-cover" />}
                </div>
                <div className="min-w-0">
                  <div className="flex items-start justify-between gap-3 sm:hidden">
                    <div className="font-serif text-xl text-espresso leading-tight">{it.name}</div>
                    <div className="font-serif text-xl text-espresso whitespace-nowrap">₹{it.price * it.quantity}</div>
                  </div>
                  <div className="font-serif text-2xl text-espresso hidden sm:block">{it.name}</div>
                  {it.flavor && <div className="text-sm text-espresso-light mt-1">{it.flavor}</div>}
                  {it.category === "boxes" ? (
                    <BoxBreakdown selections={it.selections} />
                  ) : (
                    it.selections?.length > 0 && <div className="text-sm text-espresso-light mt-1">{it.selections.join(", ")}</div>
                  )}
                  <div className="mt-4 flex items-center gap-4">
                    <div className="flex items-center gap-1 border border-line rounded-full">
                      <button onClick={() => updateQty(it._key, it.quantity - 1)} data-testid={`cart-dec-${it.slug}`} aria-label="Decrease quantity" className="tap-target text-espresso"><Minus size={16} strokeWidth={1.5} /></button>
                      <span className="text-sm w-6 text-center">{it.quantity}</span>
                      <button onClick={() => updateQty(it._key, it.quantity + 1)} data-testid={`cart-inc-${it.slug}`} aria-label="Increase quantity" className="tap-target text-espresso"><Plus size={16} strokeWidth={1.5} /></button>
                    </div>
                    <button onClick={() => removeItem(it._key)} className="text-xs text-espresso-light hover:text-espresso py-2 px-1" data-testid={`cart-remove-${it.slug}`}>Remove</button>
                  </div>
                  <Upsell item={it} />
                </div>
                <div className="hidden sm:block font-serif text-2xl text-espresso whitespace-nowrap">₹{it.price * it.quantity}</div>
              </div>
            ))}
            {/* Aggregate bundle savings caption */}
            {items.some((x) => x.savings > 0) && (
              <div className="pt-1 flex items-center gap-2 text-sm text-gold-hover" data-testid="cart-bundle-savings-caption">
                <span className="label-xs">Bundle savings applied</span>
                <span className="font-serif">
                  −₹{items.reduce((s, x) => s + (x.savings || 0) * x.quantity, 0)}
                </span>
              </div>
            )}
          </div>

          <aside className="md:col-span-4 md:sticky md:top-28 h-fit border border-line p-6 md:p-8">
            <div className="label-xs text-espresso-light">Summary</div>
            <div className="mt-6 space-y-3 text-espresso">
              <WelcomeOfferBanner variant="compact" />
              <LaunchPromoBanner variant="compact" />
              {items.some((x) => x.savings > 0) && (
                <div className="flex justify-between text-gold-hover" data-testid="cart-page-bundle-savings">
                  <span>Bundle savings</span>
                  <span className="font-serif text-lg">
                    −₹{items.reduce((s, x) => s + (x.savings || 0) * x.quantity, 0)}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span className="font-serif text-xl" data-testid="cart-page-subtotal">₹{subtotal}</span>
              </div>
              <p className="text-xs text-espresso-light">Delivery, taxes and promo applied at checkout.</p>
            </div>
            {!isOpen ? (
              <div
                data-testid="cart-store-closed"
                className="mt-8 border border-line bg-ivory-100 px-5 py-4 text-center text-sm font-serif italic text-espresso"
              >
                {storeStatus?.closed_message || "We are closed today for baking! See you on Tuesday."}
              </div>
            ) : (
              <Link to="/checkout" className="btn-espresso block text-center mt-8" data-testid="cart-page-checkout">
                Proceed to checkout
              </Link>
            )}
            <Link to="/menu" className="mt-4 block text-center label-xs text-espresso-light hover:text-espresso transition-colors" data-testid="cart-continue-shopping">Continue shopping</Link>
          </aside>
        </div>
      )}
      {items.length > 0 && isOpen && (
        <div
          data-testid="mobile-cart-checkout-bar"
          className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-between gap-4 border-t border-line bg-ivory/95 px-4 py-3 backdrop-blur-xl md:hidden"
        >
          <div>
            <div className="label-xs text-espresso-light">Subtotal</div>
            <div className="font-serif text-2xl text-espresso">₹{subtotal}</div>
          </div>
          <Link to="/checkout" data-testid="mobile-cart-checkout" className="btn-espresso flex-1 px-4 text-center">
            Checkout
          </Link>
        </div>
      )}
    </main>
  );
}
